import { useCallback } from 'react';
import { useNavigate } from 'react-router';
import { DateTools, TTableRow, Utils } from '@treezor/design-system';

import { DashboardHelpers } from '@/utils/DashboardHelpers';

import { IWallet } from '@/features/wallets/store/models/wallet.model';
import { ALL_WALLETS_PER_PAGE } from '@/features/wallets/constants';
import { allWalletsHeadersList, TAllWalletsRowType } from '@/features/wallets/store/models/allWallets.model';
import { IAgentProfileWallet } from '@/features/wallets/store/types/agent-wallets.types';

import { WalletStatus } from '@/features/wallets/components/WalletDetails/WalletStatus';
import { WalletBic } from '@/features/wallets/components/WalletDetails/WalletBic';
import { WalletMenu } from '@/features/wallets/components/WalletMenu';

import { CellLink } from '@/ui-kit/table-filters/CellLink';
import { RowSkeleton } from '@/ui-kit/loaders/RowSkeleton';

import { useI18nLocale } from '@/hooks/useI18nLocale';

interface IAllCWalletsRowsProps {
  profileWallets?: IAgentProfileWallet[];
  isLoading?: boolean;
}

export function AllWalletsRows({ profileWallets = [], isLoading = false }: IAllCWalletsRowsProps) {
  const navigate = useNavigate();
  const locale = useI18nLocale();

  const handleNavigateToProfile = useCallback(
    (wallet: IWallet, type: 'user' | 'wallet') => {
      navigate(
        DashboardHelpers.generateProfilePath(wallet.userId, type === 'user' ? {} : { walletId: wallet.walletId }),
      );
    },
    [navigate],
  );

  if (isLoading) {
    return RowSkeleton<TAllWalletsRowType>({ headers: allWalletsHeadersList, length: ALL_WALLETS_PER_PAGE });
  }

  return profileWallets?.map((profileWallet: IAgentProfileWallet): TTableRow<TAllWalletsRowType> => {
    const wallet = profileWallet.wallet;
    return {
      onClick: () => handleNavigateToProfile(wallet, 'wallet'),
      iban: (
        <div className="flex flex-col justify-start">
          <span
            className="w-fit typo-body-semibold-small cursor-pointer hover:text-interactive"
            onClick={() => handleNavigateToProfile(wallet, 'wallet')}
          >
            {wallet.iban}
          </span>
          <span className="typo-body-regular-caption">ID #{wallet.walletId}</span>
        </div>
      ),
      eventName: <>{wallet.eventName}</>,
      walletStatus: <WalletStatus status={wallet.walletStatus} />,
      userId: (
        <CellLink
          label={`${wallet.userId}`}
          handleNavigate={() => handleNavigateToProfile(wallet, 'user')}
          testId="userId"
        />
      ),
      bic: <WalletBic bic={wallet.bic} />,
      solde: <>{Utils.currencyFormater(locale, wallet.solde, 'EUR')}</>,
      authorizedBalance: <>{Utils.currencyFormater(locale, wallet.authorizedBalance, 'EUR')}</>,
      createdDate: <>{DateTools.format(wallet.createdDate)}</>,
      actions: (
        <WalletMenu
          wallet={wallet}
          user={wallet.userId}
          profileWallet={profileWallet}
          options={[
            'accountDetails',
            'statement',
            'balanceCertificate',
            'closureCertificate',
            'domiciliationCertificate',
          ]}
        />
      ),
    };
  });
}
